import { SERVER_ADDRESS_HTTP } from "./constants";

/**
 * Photo looks: a player takes a picture with their webcam and uses it as their look. The server keeps the picture
 * in memory only, for as long as the player stays in the lobby, and serves it to the lobby's players.
 */

/** The shape of the id of a photo look (the server makes them up). */
export const PHOTO_ICON_PATTERN = /^ph_[0-9a-f]{12}$/;

/** Width and height of the picture that is sent (it is shown in small circles, so this is plenty). */
export const PHOTO_SIZE = 160;
/** JPEG quality of the picture that is sent; the result is about 8-12 KB. */
export const PHOTO_QUALITY = 0.82;

export function photoIconUrl(id: string): string {
  return SERVER_ADDRESS_HTTP + "/photo-icon?id=" + encodeURIComponent(id);
}

/** Size of the square picture that is worked on (it is made smaller before it is sent). */
export const WORK_SIZE = 320;

/**
 * Cuts the largest centred square out of a picture of any size and scales it to {@code WORK_SIZE}.
 * @param mirror true to flip it left to right (a front camera shows a mirror image while you pose).
 */
export function squareCanvas(source: CanvasImageSource, width: number, height: number, mirror: boolean): HTMLCanvasElement {
  const side = Math.min(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = WORK_SIZE;
  canvas.height = WORK_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("The browser cannot draw the picture.");
  }
  if (mirror) {
    ctx.translate(WORK_SIZE, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(source, (width - side) / 2, (height - side) / 2, side, side, 0, 0, WORK_SIZE, WORK_SIZE);
  return canvas;
}

/** The picture as a small JPEG, as a Base64 string without the "data:" prefix. */
export function toJpeg(canvas: HTMLCanvasElement): string {
  const small = document.createElement("canvas");
  small.width = PHOTO_SIZE;
  small.height = PHOTO_SIZE;
  const ctx = small.getContext("2d");
  if (!ctx) {
    throw new Error("The browser cannot draw the picture.");
  }
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(canvas, 0, 0, PHOTO_SIZE, PHOTO_SIZE);
  return small.toDataURL("image/jpeg", PHOTO_QUALITY).split(",")[1];
}
